import React, { useEffect, useState } from 'react';
import { PencilIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, CheckIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { checkApi } from '../../Authentication/AxiosInstance';


const SDProfile = ({ 
  candidateData,
  onSaveProfile,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(true); // Profile section expanded by default
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const validationSchema = yup.object({
    candidate_firstname: yup.string().trim().optional(),
    candidate_middlename: yup.string().trim().optional(),
    candidate_lastname: yup.string().trim().optional(),
    candidate_mobile: yup
      .string()
      .matches(/^\+?[0-9]{7,15}$/g, 'Enter a valid mobile number')
      .optional(),
    candidate_domicile: yup.string().trim().required('Domicile state is required'),
    candidate_hsc_district: yup.string().trim().required('HSC district is required'),
    candidate_home_university: yup.string().trim().required('Home university is required'),
    candidate_management_quota: yup.boolean().optional(),
  });

  const { register, handleSubmit, reset, getValues, setValue, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      candidate_firstname: '',
      candidate_middlename: '',
      candidate_lastname: '',
      candidate_mobile: '',
      candidate_domicile: '',
      candidate_hsc_district: '',
      candidate_home_university: '',
      candidate_management_quota: false,
    }
  });

  const handleEdit = () => {
    // Parse the system-generated name if individual fields don't exist
    const nameParts = candidateData.candidate_name?.split(' ') || [];
    reset({
      candidate_firstname: candidateData.candidate_firstname || nameParts[0] || '',
      candidate_middlename: candidateData.candidate_middlename || nameParts[1] || '',
      candidate_lastname: candidateData.candidate_lastname || nameParts[2] || '',
      candidate_mobile: candidateData.candidate_mobile || '',
      candidate_domicile: (
        candidateData.candidate_domicile_state
        || candidateData?.domicile_state?.id
        || ''
      ),
      candidate_hsc_district: candidateData.candidate_hsc_district || candidateData.hsc_district?.id || '',
      candidate_home_university: candidateData.candidate_home_university || '',
      candidate_management_quota: Boolean(candidateData.candidate_management_quota) || false,
    });
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleDistrictChange = (e) => {
    const selectedCityId = e.target.value;
    const selectedCity = cities.find(city => city.id === parseInt(selectedCityId));
    
    if (selectedCity) {
      // Update the form with the selected district and auto-populate home university
      setValue('candidate_hsc_district', selectedCityId);
      setValue('candidate_home_university', selectedCity.city_university || '');
    }
  };

  const onSubmit = async (formData) => {
    // Additional validation for required domicile state
    if (!formData.candidate_domicile || formData.candidate_domicile === '') {
      setError('Domicile state is required. Please select a state.');
      return;
    }
    
    console.log('DEBUG SDProfile: Form data being submitted:', formData);
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    try {
      await onSaveProfile(formData);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Keep form values in sync if candidateData updates while not editing
  useEffect(() => {
    if (!isEditing && candidateData) {
      // Parse the system-generated name if individual fields don't exist
      const nameParts = candidateData.candidate_name?.split(' ') || [];
      reset({
        candidate_firstname: candidateData.candidate_firstname || nameParts[0] || '',
        candidate_middlename: candidateData.candidate_middlename || nameParts[1] || '',
        candidate_lastname: candidateData.candidate_lastname || nameParts[2] || '',
        candidate_mobile: candidateData.candidate_mobile || '',
        candidate_domicile: (
          candidateData.candidate_domicile_state
          || candidateData?.domicile_state?.id
          || ''
        ),
        candidate_hsc_district: candidateData.candidate_hsc_district || candidateData.hsc_district?.id || '',
        candidate_home_university: candidateData.candidate_home_university || '',
        candidate_management_quota: Boolean(candidateData.candidate_management_quota) || false,
      });
    }
  }, [candidateData, isEditing, reset]);

  // Update form values when cities are loaded (for proper dropdown selection)
  useEffect(() => {
    if (isEditing && cities.length > 0 && candidateData.hsc_district?.id) {
      setValue('candidate_hsc_district', candidateData.hsc_district.id);
    }
  }, [cities, isEditing, candidateData.hsc_district?.id, setValue]);

  // Update form values when states are loaded (for proper dropdown selection)
  useEffect(() => {
    if (isEditing && states.length > 0 && candidateData.domicile_state?.id) {
      setValue('candidate_domicile', candidateData.domicile_state.id);
    }
  }, [states, isEditing, candidateData.domicile_state?.id, setValue]);

  // Fetch states for domicile dropdown
  useEffect(() => {
      setStatesLoading(true);
      checkApi
        .get('/masters/states/')
        .then((res) => {
          const data = res.data;
          setStates(Array.isArray(data) ? data : (data?.results || []));
        })
        .catch(() => {})
        .finally(() => setStatesLoading(false));
      
      // Fetch cities from Maharashtra for HSC district dropdown
      setCitiesLoading(true);
        checkApi
          .get('/cities/?state=Maharashtra')
          .then((res) => {
            const data = res.data;
            setCities(Array.isArray(data) ? data : (data?.results || []));
          })
          .catch(() => {})
          .finally(() => setCitiesLoading(false));
  }, [isEditing]);

  if (!candidateData) return null;
  console.log(candidateData);	
  return (
    <div className="rounded-lg shadow-lg shadow-primary-100">
      <div className="bg-tertiary-600 rounded-xl pb-4 shadow-md shadow-green-300">


        {/* Header */}
        <div className="flex items-start justify-between shadow-md shadow-green-300 p-3 rounded-lg">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">My Profile</h3>
                </div>
            </div>
            
            {/* Edit Button and Collapse/Expand Button */}
            <div className="flex items-center space-x-2">
              {/* Edit Button */}
              {!isEditing && (
                <button
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 
                        text-white rounded-lg hover:bg-green-800 transition-colors h-10"
                >
                  <PencilIcon className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              )}
              {/* Collapse/Expand Button */}
              {!isEditing && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-lg hover:bg-green-800 transition-colors"
                  title={isExpanded ? "Collapse" : "Expand"}
                >
                  {isExpanded ? (
                      <ChevronUpIcon className="w-5 h-5" />
                  ) : (
                      <ChevronDownIcon className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
        </div>


        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Profile Details */}
        {isExpanded && !isEditing && (
          <div className="space-y-4 pl-4 pr-4 pt-4 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <p className="text-gray-900">
                  {(candidateData?.candidate_firstname.toUpperCase() || '') + ' ' + 
                  (candidateData?.candidate_middlename.toUpperCase() || '') + ' ' + 
                  (candidateData?.candidate_lastname.toUpperCase() || '')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                <p className="text-gray-900">{candidateData.candidate_mobile|| 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HSC District</label>
                <p className="text-gray-900">{candidateData.hsc_district?.city_names?.[0] || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Home University</label>
                <p className="text-gray-900">{candidateData.candidate_home_university || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domicile State</label>
                <p className="text-gray-900">{candidateData.domicile_state?.state_name || 'Not set'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Edit Form */}
        {isExpanded && isEditing && (
          <form onSubmit={handleSubmit(onSubmit)} 
                className="space-y-6">
                {/* Full Name */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      {...register('candidate_firstname')}
                      placeholder="First name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    {errors.candidate_firstname && (
                      <p className="text-red-600 text-sm mt-1">{errors.candidate_firstname.message}</p>
                    )}
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                    <input
                      type="text"
                      {...register('candidate_middlename')}
                      placeholder="Middle name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      {...register('candidate_lastname')}
                      placeholder="Last name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    {errors.candidate_lastname && (
                      <p className="text-red-600 text-sm mt-1">{errors.candidate_lastname.message}</p>
                    )}
                  </div>
                </div>
                {/* Mobile Number */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                          <input
                            type="tel"
                            {...register('candidate_mobile')}
                            placeholder="Mobile (10 digits)"
                            inputMode="numeric"
                            pattern="^[0-9]{10}$"
                            maxLength={10}
                            className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          />
                          {errors.candidate_mobile && (
                            <p className="text-red-600 text-sm mt-1">{errors.candidate_mobile.message}</p>
                          )}
                    </div>
                  </div>
                </div>
                {/* HSC District & Home University */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                    please select the district where you completed your 12th (HSC).
                    (Required only if you plan to apply through MHTCET)
                    </label>
                    <select
                          {...register('candidate_hsc_district')}
                          onChange={handleDistrictChange}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors.candidate_hsc_district 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                        >
                          <option value="" disabled>
                            {citiesLoading ? 'Loading cities…' : 'Select HSC district'}
                          </option>
                          {cities.map((s) => (
                            <option key={s.id || s.city_name} value={s.id}>{s.city_names && s.city_names.length > 0 ? s.city_names[0] : s.city_code}</option>
                          ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Home University</label>
                    <input
                          {...register('candidate_home_university')}
                          type="text"
                          placeholder="Auto-populated from district selection"
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-200"
                    />
                  </div>
                </div>
                {/* Domicile State */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="col-span-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Domicile State *</label>
                        <select
                          {...register('candidate_domicile')}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            errors.candidate_domicile 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300'
                          }`}
                        >
                          <option value="" disabled>
                            {statesLoading ? 'Loading states…' : 'Select domicile state'}
                          </option>
                          {states.map((s) => (
                            <option key={s.id || s.state_abbrev} value={s.id}>{s.state_name}</option>
                          ))}
                        </select>
                        {errors.candidate_domicile && (
                          <p className="text-red-600 text-sm mt-1">{errors.candidate_domicile.message}</p>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('candidate_management_quota')}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Open for Management Seat
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:space-x-4 pt-4">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className="buttonClass w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="buttonClass w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-2 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SDProfile;